import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { RealtimeClient } from './RealtimeClient';
import { RealtimeEvent } from './types';

type Ctx = {
  client: RealtimeClient | null;
  isConnected: boolean;
};

const RealtimeContext = createContext<Ctx>({ client: null, isConnected: false });

type RealtimeProviderProps = React.PropsWithChildren<{
  baseUrl: string;
  token?: string | null;
}>;

export const RealtimeProvider: React.FC<RealtimeProviderProps> = ({
  baseUrl,
  token,
  children,
}) => {
  const clientRef = useRef<RealtimeClient | null>(null);
  const [isConnected, setIsConnected] = useState(false);

  useEffect(() => {
    if (!clientRef.current) {
      clientRef.current = new RealtimeClient(baseUrl);
      clientRef.current.onEvent((evt: RealtimeEvent) => {
        if (evt?.type === 'ping') return;
      });
    }

    // Connect with token if available, otherwise connect anonymously
    clientRef.current.connect(token || '');

    const id = setInterval(
      () => setIsConnected(!!clientRef.current?.isConnected),
      1000,
    );
    return () => clearInterval(id);
  }, [baseUrl, token]);

  const value = useMemo(
    () => ({ client: clientRef.current, isConnected }),
    [isConnected],
  );
  return (
    <RealtimeContext.Provider value={value}>
      {children}
    </RealtimeContext.Provider>
  );
};

export const useRealtime = () => useContext(RealtimeContext);

export const useSubscribeTopic = (
  topic: string,
  handler: (evt: RealtimeEvent) => void,
) => {
  const { client } = useRealtime();
  useEffect(() => {
    if (!client || !topic) return;
    const off = client.onEvent(evt => {
      handler(evt);
    });
    client.subscribe([topic]);
    return () => {
      client.unsubscribe([topic]);
      off();
    };
  }, [client, topic, handler]);
};
